const API_BASE = import.meta.env.VITE_SOP_API_BASE || "http://localhost:3000/api";

function getTokens() {
  try {
    return JSON.parse(localStorage.getItem("sop_tokens") || "null");
  } catch {
    return null;
  }
}

function setTokens(tokens) {
  if (tokens) localStorage.setItem("sop_tokens", JSON.stringify(tokens));
  else localStorage.removeItem("sop_tokens");
}

async function refreshTokens() {
  const current = getTokens();
  if (!current?.refreshToken) return null;
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) {
    setTokens(null);
    return null;
  }
  const data = await res.json();
  setTokens(data);
  return data;
}

async function request(path, { method = "GET", body, skipAuth = false, _retried = false } = {}) {
  const tokens = getTokens();
  const headers = { "Content-Type": "application/json" };
  if (!skipAuth && tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !skipAuth && !_retried) {
    const refreshed = await refreshTokens();
    if (refreshed) return request(path, { method, body, skipAuth, _retried: true });
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    // no body
  }

  if (!res.ok) {
    const message = data?.message || `Eroare ${res.status}`;
    throw new Error(Array.isArray(message) ? message.join(" ") : message);
  }
  return data;
}

function qs(params) {
  const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}

export const api = {
  login: async (email, password) => {
    const data = await request("/auth/login", { method: "POST", body: { email, password }, skipAuth: true });
    setTokens(data);
    return data;
  },
  logout: async () => {
    const tokens = getTokens();
    if (tokens?.refreshToken) {
      await request("/auth/logout", { method: "POST", body: { refreshToken: tokens.refreshToken } }).catch(() => {});
    }
    setTokens(null);
  },
  getStoredUser: () => getTokens()?.user ?? null,
  isAuthenticated: () => !!getTokens()?.accessToken,

  users: {
    list: () => request("/users"),
    create: (data) => request("/users", { method: "POST", body: data }),
    update: (id, data) => request(`/users/${id}`, { method: "PATCH", body: data }),
  },

  clients: {
    list: (includeArhivate) => request(`/clients${qs({ includeArhivate })}`),
    get: (id) => request(`/clients/${id}`),
    create: (data) => request("/clients", { method: "POST", body: data }),
    update: (id, data) => request(`/clients/${id}`, { method: "PATCH", body: data }),
    getStorage: (id) => request(`/clients/${id}/stocare`),
  },

  sites: {
    list: (clientId) => request(`/sites${qs({ clientId })}`),
    create: (data) => request("/sites", { method: "POST", body: data }),
    update: (id, data) => request(`/sites/${id}`, { method: "PATCH", body: data }),
  },

  procese: {
    list: () => request("/procese"),
    create: (data) => request("/procese", { method: "POST", body: data }),
  },

  liniiMajore: {
    list: () => request("/linii-majore"),
    create: (data) => request("/linii-majore", { method: "POST", body: data }),
  },

  documente: {
    list: (filters) => request(`/documente${qs(filters)}`),
    get: (id) => request(`/documente/${id}`),
    istoric: (id) => request(`/documente/${id}/istoric`),
    create: (data) => request("/documente", { method: "POST", body: data }),
    update: (id, data) => request(`/documente/${id}`, { method: "PATCH", body: data }),
    submit: (id) => request(`/documente/${id}/trimite-spre-aprobare`, { method: "POST" }),
    decideApproval: (id, data) => request(`/documente/${id}/decide-aprobare`, { method: "POST", body: data }),
    clientValidate: (id, data) => request(`/documente/${id}/co-validare-client`, { method: "POST", body: data }),
    newVersion: (id, changeDescription) => request(`/documente/${id}/versiune-noua`, { method: "POST", body: { changeDescription } }),
    archive: (id) => request(`/documente/${id}/arhiveaza`, { method: "POST" }),
    clone: (id, data) => request(`/documente/${id}/cloneaza`, { method: "POST", body: data }),
  },

  instruire: {
    getRoles: (documentId) => request(`/documente/${documentId}/instruire/roluri`),
    setRoles: (documentId, functii) => request(`/documente/${documentId}/instruire/roluri`, { method: "PUT", body: { functii } }),
    generate: (documentId, functie) => request(`/documente/${documentId}/instruire/${functie}/genereaza`, { method: "POST" }),
    getQuiz: (documentId, functie) => request(`/documente/${documentId}/instruire/${functie}`),
    listAttempts: (documentId) => request(`/documente/${documentId}/instruire-incercari`),
    plan: (functie) => request(`/instruire/plan${qs({ functie })}`),
    getQuizForTaking: (quizId) => request(`/instruire/teste/${quizId}`),
    submitAttempt: (quizId, data) => request(`/instruire/teste/${quizId}/trimite`, { method: "POST", body: data }),
  },

  anexe: {
    list: (documentId) => request(`/documente/${documentId}/anexe`),
    upload: async (documentId, file) => {
      const tokens = getTokens();
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/documente/${documentId}/anexe`, {
        method: "POST",
        headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {},
        body: formData,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        // no body
      }
      if (!res.ok) {
        const message = data?.message || `Eroare ${res.status}`;
        throw new Error(Array.isArray(message) ? message.join(" ") : message);
      }
      return data;
    },
    remove: (documentId, anexaId) => request(`/documente/${documentId}/anexe/${anexaId}`, { method: "DELETE" }),
    download: async (documentId, anexaId, filename) => {
      const tokens = getTokens();
      const res = await fetch(`${API_BASE}/documente/${documentId}/anexe/${anexaId}/descarca`, {
        headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {},
      });
      if (!res.ok) throw new Error(`Eroare ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
};
